<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import LaisConfirmModal from 'dashboard/components-next/LaisConfirmModal.vue';
import QprecoInterestsAPI from 'dashboard/api/qprecoInterests';
import ImovelAPI from 'dashboard/api/imoveis';
import { useAlert } from 'dashboard/composables';
import PricingReportModule from 'dashboard/components-next/qpreco/PricingReportModule.vue';
import PricingReportModal from 'dashboard/components-next/qpreco/PricingReportModal.vue';
import ValuationReviewPanel from 'dashboard/components-next/qpreco/ValuationReviewPanel.vue';
import DiligenceReviewPanel from 'dashboard/components-next/diligence/DiligenceReviewPanel.vue';
import QprecoClaimCondoBanner from '../components/QprecoClaimCondoBanner.vue';
import QprecoDetailSkeleton from '../components/QprecoDetailSkeleton.vue';
import QprecoDetailIdentityCard from '../components/QprecoDetailIdentityCard.vue';
import QprecoDetailTabs from '../components/QprecoDetailTabs.vue';
import QprecoEstimatePanel from '../components/QprecoEstimatePanel.vue';
import QprecoSuggestionsCard from '../components/QprecoSuggestionsCard.vue';
import QprecoPriceHistoryPanel from '../components/QprecoPriceHistoryPanel.vue';
import QprecoCondominiumPanel from '../components/QprecoCondominiumPanel.vue';
import QprecoValuesCard from '../components/QprecoValuesCard.vue';
import QprecoSimilarCard from '../components/QprecoSimilarCard.vue';
import QprecoEstimateSummaryCard from '../components/QprecoEstimateSummaryCard.vue';
import QprecoSharesCard from '../components/QprecoSharesCard.vue';
import QprecoListHero from '../components/QprecoListHero.vue';
import QprecoListFilters from '../components/QprecoListFilters.vue';
import QprecoListEmptyState from '../components/QprecoListEmptyState.vue';
import QprecoInterestCard from '../components/QprecoInterestCard.vue';
import QprecoListPagination from '../components/QprecoListPagination.vue';
import QprecoComplementModal from '../components/QprecoComplementModal.vue';
import { detailTabs, PAGE_SIZE } from '../utils/qprecoConstants';
import { compactQuery, formatNumber } from '../utils/qprecoFormatters';
import {
  completionScore,
  isExternalItem,
  needsComplement,
  propertyHistoryCount,
  propertyIdentity,
  propertySourceUrl,
} from '../utils/qprecoProperty';
import { useQprecoListQuery } from '../composables/useQprecoListQuery';
import { useQprecoDetailData } from '../composables/useQprecoDetailData';
import { useQprecoPricing } from '../composables/useQprecoPricing';
import { useQprecoValuationShares } from '../composables/useQprecoValuationShares';
import { useQprecoPricingReport } from '../composables/useQprecoPricingReport';

const router = useRouter();
const route = useRoute();
const store = useStore();
const accountId = computed(() => store.getters.getCurrentAccountId);
const activeInterestId = computed(() =>
  route.params.id ? String(route.params.id) : ''
);
const isDetailMode = computed(() => Boolean(activeInterestId.value));
const DETAIL_SECTIONS = [
  'estimativa',
  'detalhado',
  'similares',
  'condominio',
  'cma',
  'diligencia',
];
const activeDetailSection = computed(() => {
  const section = route.params.section ? String(route.params.section) : '';
  return DETAIL_SECTIONS.includes(section) ? section : 'estimativa';
});
const showEstimatePanel = computed(() =>
  ['estimativa', 'detalhado'].includes(activeDetailSection.value)
);
const showHistoryPanel = computed(
  () => activeDetailSection.value === 'detalhado'
);
const showSimilarPanel = computed(
  () => activeDetailSection.value === 'similares'
);
const showCondominiumPanel = computed(
  () => activeDetailSection.value === 'condominio'
);
const showValuationReviewPanel = computed(
  () => activeDetailSection.value === 'cma'
);
const showDiligencePanel = computed(
  () => activeDetailSection.value === 'diligencia'
);

const interests = ref([]);
const listMeta = ref({});
const externalMeta = ref({});
const loading = ref(false);
const loadError = ref(false);
const detail = ref(null);
const detailLoading = ref(false);
const detailError = ref(false);
const pendingDelete = ref(null);
const showDelete = ref(false);
const isDeleting = ref(false);
const claimCondoPending = ref(false);
const claimCondoSlug = ref(null);
const complementTarget = ref(null);
const showComplement = ref(false);
const isSavingComplement = ref(false);
const complementForm = ref({ complemento: '', cep: '', numero: '' });
const syncingHistory = ref(false);
const claimCondoMessage = computed(() =>
  claimCondoSlug.value
    ? `Reivindicação de condomínio identificada: ${claimCondoSlug.value}. Conclua o vínculo completando os dados do imóvel.`
    : 'Reivindicação de condomínio identificada. Conclua o vínculo completando os dados do imóvel.'
);

const {
  searchInput,
  cityInput,
  neighborhoodInput,
  sourceInput,
  purposeInput,
  queryPage,
  queryFilters,
  totalResults,
  totalPages,
  visiblePages,
  sourceLabel,
  sourceHelper,
  activeFilterChips,
  activeFiltersCount,
  resultRangeLabel,
  applyFilters,
  clearFilters,
  removeFilter,
  selectSource,
  goToPage,
} = useQprecoListQuery({ router, route, accountId, listMeta });

const {
  showAllCondominiumHistory,
  detailInterest,
  estimate,
  condominiumSummary,
  hasCondominiumSummary,
  condominiumLocation,
  similarItems,
  priceHistory,
  priceHistoryMeta,
  priceHistoryCount,
  priceHistorySources,
  priceHistoryQueryLabel,
  isRentFlow,
  condominiumPriceHistory,
  condominiumPriceHistoryCount,
  condominiumPriceHistoryLoading,
  condominiumPriceHistoryError,
  condominiumPriceHistorySources,
  hasCondominiumHistoryExternalSource,
  condominiumPriceHistoryQueryLabel,
  hasCondominiumSectionData,
  visibleCondominiumPriceHistory,
  canExpandCondominiumPriceHistory,
  condominiumStats,
} = useQprecoDetailData({ detail, detailLoading, showCondominiumPanel });

const {
  editingPrice,
  savingPrice,
  priceInput,
  priceError,
  suggestedPrice,
  fasterSuggestion,
  maxSuggestion,
  currentPrice,
  rulerPercentiles,
  rulerMinPrice,
  rulerMaxPrice,
  rulerMarkerValue,
  rulerMarkerColor,
  rulerVariant,
  pricePerArea,
  priceQuestion,
  confidenceTitle,
  confidenceBody,
  priceSectionTitle,
  priceHeading,
  showPositivePriceAlert,
  showHighPriceAlert,
  priceRangeLabel,
  priceRows,
  startEditPrice,
  cancelEditPrice,
  savePrice,
} = useQprecoPricing({
  detailInterest,
  estimate,
  isRentFlow,
  onPriceSaved: () => fetchDetailed(activeInterestId.value),
});

const {
  sharesLoading,
  sharesError,
  creatingShare,
  shareCreateForm,
  showRevokeShare,
  revokingShare,
  activeValuationShares,
  currentIssuedShare,
  canManageValuationShares,
  shareListSections,
  resetShareState,
  fetchValuationShares,
  createValuationShare,
  copyIssuedShareUrl,
  requestRevokeShare,
  cancelRevokeShare,
  confirmRevokeShare,
} = useQprecoValuationShares({ activeInterestId, detailInterest });

const listSummaryCards = computed(() => [
  {
    key: 'results',
    label: 'Imóveis encontrados',
    value: totalResults.value,
    icon: 'i-lucide-building-2',
    tone: 'bg-lais-primary-50 text-lais-primary ring-lais-primary-50',
  },
  {
    key: 'visible',
    label: 'Nesta página',
    value: interests.value.length,
    icon: 'i-lucide-list-checks',
    tone: 'bg-sky-50 text-sky-700 ring-sky-100',
  },
  {
    key: 'histories',
    label: 'Históricos carregados',
    value: interests.value.reduce(
      (sum, item) => sum + propertyHistoryCount(item),
      0
    ),
    icon: 'i-lucide-history',
    tone: 'bg-emerald-50 text-emerald-700 ring-emerald-100',
  },
]);

const fetchInterests = async () => {
  loading.value = true;
  loadError.value = false;
  try {
    const filters = queryFilters.value;
    const { data } = await QprecoInterestsAPI.get({
      page: queryPage.value,
      per_page: PAGE_SIZE,
      external_page: queryPage.value,
      external_page_size: PAGE_SIZE,
      history_limit: 20,
      q: filters.q,
      cidade: filters.cidade,
      bairro: filters.bairro,
      finalidade: filters.finalidade,
      source: filters.source,
    });
    interests.value = data.data || data || [];
    listMeta.value = data.meta || {};
    externalMeta.value = data.meta?.external || {};
  } catch (error) {
    loadError.value = true;
    interests.value = [];
    listMeta.value = {};
    externalMeta.value = {};
  } finally {
    loading.value = false;
  }
};

const fetchDetailed = async id => {
  detailLoading.value = true;
  detailError.value = false;
  editingPrice.value = false;
  priceError.value = '';
  showAllCondominiumHistory.value = false;

  try {
    const { data } = await QprecoInterestsAPI.detailed(id, {
      history_limit: 100,
      historyMode: 'fast',
    });
    detail.value = data;
    await fetchValuationShares(id);
  } catch (error) {
    detailError.value = true;
    detail.value = null;
    resetShareState();
  } finally {
    detailLoading.value = false;
  }
};

onMounted(() => {
  if (route.query.claim_condo) {
    claimCondoPending.value = true;
    claimCondoSlug.value = route.query.claim_condo;
    try {
      window.localStorage.setItem(
        'lais_claim_condo_pending',
        String(route.query.claim_condo)
      );
    } catch (error) {
      // Storage failures should not block the dashboard flow.
    }
  } else {
    try {
      const stored = window.localStorage.getItem('lais_claim_condo_pending');
      if (stored) {
        claimCondoPending.value = true;
        claimCondoSlug.value = stored;
      }
    } catch (error) {
      // Storage failures should not block the dashboard flow.
    }
  }
});

watch(
  [
    accountId,
    activeInterestId,
    queryPage,
    () => route.query.q,
    () => route.query.cidade,
    () => route.query.bairro,
    () => route.query.finalidade,
    () => route.query.source,
  ],
  ([currentAccountId, interestId]) => {
    if (!currentAccountId) return;
    if (interestId) {
      fetchDetailed(interestId);
    } else {
      resetShareState();
      fetchInterests();
    }
  },
  { immediate: true }
);

const dismissClaimCondo = () => {
  claimCondoPending.value = false;
  claimCondoSlug.value = null;
  try {
    window.localStorage.removeItem('lais_claim_condo_pending');
  } catch (error) {
    // ignore
  }
};

const exploreCondos = () => {
  router.push({
    path: '/lais/condominios',
    query: { from_route: 'qpreco_header' },
  });
};

const requestComplement = item => {
  const identity = propertyIdentity(item);
  complementTarget.value = item;
  complementForm.value = {
    complemento: item?.address_complement || item?.complemento || '',
    cep: identity?.cep || item?.cep || '',
    numero: item?.street_number || item?.numero || '',
  };
  showComplement.value = true;
};

const cancelComplement = () => {
  if (isSavingComplement.value) return;
  complementTarget.value = null;
  showComplement.value = false;
};

const submitComplement = async () => {
  if (!complementTarget.value) return;
  isSavingComplement.value = true;
  try {
    const imovelId =
      complementTarget.value.imovel_id || complementTarget.value.id;
    await ImovelAPI.completeAddress(imovelId, complementForm.value);
    useAlert('Endereço atualizado com sucesso.');
    showComplement.value = false;
    complementTarget.value = null;
    if (isDetailMode.value) {
      await fetchDetailed(activeInterestId.value);
    } else {
      await fetchInterests();
    }
  } catch (error) {
    useAlert('Não foi possível salvar agora. Tente novamente.');
  } finally {
    isSavingComplement.value = false;
  }
};

const handleAddImovel = () => {
  router.push({
    name: 'lais_imoveis_new',
    params: { accountId: accountId.value },
    query: { from_route: 'qpreco_header' },
  });
};

const goToEditCalculator = item => {
  if (!item?.id) return;
  if (isExternalItem(item)) {
    const sourceUrl = propertySourceUrl(item);
    if (sourceUrl) window.open(sourceUrl, '_blank', 'noopener');
    return;
  }
  router.push({
    name: 'imovel_edit',
    params: { accountId: accountId.value, id: item.imovel_id || item.id },
    query: { from_route: 'qpreco_header' },
  });
};

const handleAccess = item => {
  if (!item?.id) return;
  router.push({
    name: 'lais_area_qpreco_section',
    params: {
      accountId: accountId.value,
      id: item.id,
      section: 'estimativa',
    },
    query: compactQuery({ ...queryFilters.value, page: queryPage.value }),
  });
};

const goToDetailSection = section => {
  if (!activeInterestId.value) return;

  router.push({
    name: 'lais_area_qpreco_section',
    params: {
      accountId: accountId.value,
      id: activeInterestId.value,
      section,
    },
    query: compactQuery(route.query),
  });
};

const {
  showPricingReport,
  activeReportInterestId,
  pricingReportInitialSection,
  pricingReportTitle,
  pricingReportSubtitle,
  isPricingReportLoading,
  isPricingReportUnavailable,
  handlePricingReportLinkClick,
  handlePricingReportLoaded,
  handlePricingReportSectionViewed,
  handlePricingReportCtaClicked,
  closePricingReport,
} = useQprecoPricingReport({
  route,
  router,
  accountId,
  activeInterestId,
  detailInterest,
});

const goBackToIndex = () => {
  router.push({
    name: 'lais_area_qpreco_index',
    params: { accountId: accountId.value },
    query: compactQuery(route.query),
  });
};

const requestDelete = item => {
  pendingDelete.value = item;
  showDelete.value = true;
};

const cancelDelete = () => {
  if (isDeleting.value) return;
  pendingDelete.value = null;
  showDelete.value = false;
};

const confirmDelete = async () => {
  if (!pendingDelete.value) return;
  if (isExternalItem(pendingDelete.value)) return;
  isDeleting.value = true;
  try {
    await QprecoInterestsAPI.delete(pendingDelete.value.id);
    interests.value = interests.value.filter(
      item => item.id !== pendingDelete.value.id
    );
    const deletedActiveItem =
      String(pendingDelete.value.id) === String(activeInterestId.value);
    showDelete.value = false;
    pendingDelete.value = null;
    useAlert('Imóvel removido da Área QPreço.');
    if (deletedActiveItem) goBackToIndex();
  } catch (error) {
    useAlert('Não foi possível excluir agora. Tente novamente.');
  } finally {
    isDeleting.value = false;
  }
};

const syncPriceHistories = async () => {
  syncingHistory.value = true;
  try {
    const { data } = await QprecoInterestsAPI.syncPriceHistories();
    const updated = data?.updated || 0;
    useAlert(
      updated
        ? `${updated} imóveis atualizados com histórico de preços.`
        : 'Históricos verificados.'
    );
    if (isDetailMode.value) {
      await fetchDetailed(activeInterestId.value);
    } else {
      await fetchInterests();
    }
  } catch (error) {
    useAlert('Não foi possível atualizar os históricos agora.');
  } finally {
    syncingHistory.value = false;
  }
};
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div class="h-full min-h-0 overflow-y-auto bg-lais-bg-page font-redHatText">
    <LaisHeader
      title="Área QPreço"
      helper="Dados exclusivos sobre o mercado para decidir preço, comparar similares e revisar o histórico por logradouro e número."
    >
      <template #actions>
        <LaisPrimaryButton
          v-if="isDetailMode"
          variant="ghost"
          icon="i-lucide-arrow-left"
          @click="goBackToIndex"
        >
          Voltar
        </LaisPrimaryButton>
        <LaisPrimaryButton
          variant="ghost"
          icon="i-lucide-refresh-cw"
          :disabled="syncingHistory"
          @click="syncPriceHistories"
        >
          {{ syncingHistory ? 'Atualizando...' : 'Atualizar históricos' }}
        </LaisPrimaryButton>
        <LaisPrimaryButton icon="i-lucide-plus" @click="handleAddImovel">
          Adicionar imóvel
        </LaisPrimaryButton>
      </template>
    </LaisHeader>

    <main
      class="mx-auto w-full max-w-[1480px] px-3 pb-24 pt-4 sm:px-5 md:pb-8 lg:px-8"
    >
      <QprecoClaimCondoBanner
        v-if="claimCondoPending"
        :message="claimCondoMessage"
        @explore="exploreCondos"
        @dismiss="dismissClaimCondo"
      />

      <template v-if="isDetailMode">
        <QprecoDetailSkeleton v-if="detailLoading" />

        <div
          v-else-if="detailError"
          class="lais-card p-6 text-[13px] text-lais-text-muted"
        >
          Não foi possível carregar a estimativa agora.
          <LaisPrimaryButton
            variant="ghost"
            icon="i-lucide-refresh-cw"
            @click="fetchDetailed(activeInterestId)"
          >
            Tentar novamente
          </LaisPrimaryButton>
        </div>

        <div
          v-else-if="detail"
          class="grid gap-4 xl:grid-cols-[minmax(0,1.2fr)_minmax(320px,0.8fr)]"
        >
          <div class="grid gap-4 min-w-0">
            <QprecoDetailIdentityCard
              :detail-interest="detailInterest"
              :active-interest-id="activeInterestId"
              :price-history-count="priceHistoryCount"
              :price-history-sources="priceHistorySources"
              :pricing-report-loading="isPricingReportLoading(activeInterestId)"
              :pricing-report-unavailable="isPricingReportUnavailable(estimate)"
              :needs-complement="needsComplement(detailInterest)"
              :is-external="isExternalItem(detailInterest)"
              @pricing-report-click="
                payload => handlePricingReportLinkClick(payload, detailInterest)
              "
              @complement="requestComplement(detailInterest)"
              @edit-calculator="goToEditCalculator(detailInterest)"
            />

            <QprecoDetailTabs
              :tabs="detailTabs"
              :active-section="activeDetailSection"
              @select="goToDetailSection"
            />

            <ValuationReviewPanel
              v-if="showValuationReviewPanel"
              :imovel-id="detailInterest.imovel_id"
              :qpreco-interest-id="activeInterestId"
              @updated="fetchDetailed(activeInterestId)"
            />

            <DiligenceReviewPanel
              v-if="showDiligencePanel"
              :imovel-id="detailInterest.imovel_id"
              :imovel-listing-id="
                detailInterest.imovel_listing_id || detailInterest.listing_id
              "
              @updated="fetchDetailed(activeInterestId)"
            />

            <QprecoEstimatePanel
              v-if="showEstimatePanel"
              v-model:price-input="priceInput"
              :price-section-title="priceSectionTitle"
              :confidence-title="confidenceTitle"
              :confidence-body="confidenceBody"
              :price-heading="priceHeading"
              :current-price="currentPrice"
              :suggested-price="suggestedPrice"
              :max-suggestion="maxSuggestion"
              :price-per-area="pricePerArea"
              :can-edit-price="detailInterest.imovel_id"
              :editing-price="editingPrice"
              :saving-price="savingPrice"
              :price-question="priceQuestion"
              :price-error="priceError"
              :price-range-label="priceRangeLabel"
              :ruler-marker-value="rulerMarkerValue"
              :ruler-min-price="rulerMinPrice"
              :ruler-max-price="rulerMaxPrice"
              :ruler-percentiles="rulerPercentiles"
              :ruler-marker-color="rulerMarkerColor"
              :ruler-variant="rulerVariant"
              :show-positive-price-alert="showPositivePriceAlert"
              :show-high-price-alert="showHighPriceAlert"
              @start-edit-price="startEditPrice"
              @cancel-edit-price="cancelEditPrice"
              @save-price="savePrice"
            />

            <QprecoSuggestionsCard
              v-if="activeDetailSection === 'estimativa'"
              :faster-suggestion="fasterSuggestion"
              :suggested-price="suggestedPrice"
              :max-suggestion="maxSuggestion"
            />

            <QprecoPriceHistoryPanel
              v-if="showHistoryPanel"
              :price-history="priceHistory"
              :price-history-count="priceHistoryCount"
              :price-history-query-label="priceHistoryQueryLabel"
              :price-history-sources="priceHistorySources"
            />

            <QprecoCondominiumPanel
              v-if="showCondominiumPanel"
              :has-condominium-section-data="hasCondominiumSectionData"
              :has-condominium-summary="hasCondominiumSummary"
              :condominium-summary="condominiumSummary"
              :condominium-location="condominiumLocation"
              :condominium-stats="condominiumStats"
              :condominium-price-history="condominiumPriceHistory"
              :visible-condominium-price-history="
                visibleCondominiumPriceHistory
              "
              :can-expand-condominium-price-history="
                canExpandCondominiumPriceHistory
              "
              :condominium-price-history-count="condominiumPriceHistoryCount"
              :condominium-price-history-query-label="
                condominiumPriceHistoryQueryLabel
              "
              :condominium-price-history-sources="
                condominiumPriceHistorySources
              "
              :condominium-price-history-loading="
                condominiumPriceHistoryLoading
              "
              :condominium-price-history-error="condominiumPriceHistoryError"
              :has-condominium-history-external-source="
                hasCondominiumHistoryExternalSource
              "
              :is-rent-flow="isRentFlow"
              @show-all="showAllCondominiumHistory = true"
            />
          </div>

          <aside class="grid gap-4 min-w-0 content-start">
            <QprecoValuesCard
              v-if="showEstimatePanel || showHistoryPanel"
              :price-rows="priceRows"
            />

            <QprecoSimilarCard
              v-if="showEstimatePanel || showSimilarPanel"
              :similar-items="similarItems"
              :fallback-neighborhood="detailInterest.bairro"
              :is-rent-flow="isRentFlow"
            />

            <QprecoEstimateSummaryCard
              :estimate="estimate"
              :completion-score="completionScore(detailInterest)"
              :queries-count="detailInterest.queries_count"
              :price-history-meta="priceHistoryMeta"
              :price-history="priceHistory"
              :price-history-sources="priceHistorySources"
              :can-delete="!isExternalItem(detailInterest)"
              @delete="requestDelete(detailInterest)"
            />

            <QprecoSharesCard
              v-if="canManageValuationShares"
              v-model:expires-in-days="shareCreateForm.expiresInDays"
              v-model:view-limit="shareCreateForm.viewLimit"
              :active-shares-count="activeValuationShares.length"
              :creating-share="creatingShare"
              :current-issued-share="currentIssuedShare"
              :shares-loading="sharesLoading"
              :shares-error="sharesError"
              :share-list-sections="shareListSections"
              @create="createValuationShare"
              @copy="copyIssuedShareUrl"
              @reload="fetchValuationShares(activeInterestId)"
              @revoke="requestRevokeShare"
            />
          </aside>
        </div>
      </template>

      <template v-else>
        <div class="grid gap-4">
          <QprecoListHero
            :source-helper="sourceHelper"
            :source-label="sourceLabel"
            :total-results="totalResults"
            :list-summary-cards="listSummaryCards"
          />

          <QprecoListFilters
            v-model:search-input="searchInput"
            v-model:city-input="cityInput"
            v-model:neighborhood-input="neighborhoodInput"
            v-model:purpose-input="purposeInput"
            :source-input="sourceInput"
            :active-filters-count="activeFiltersCount"
            :active-filter-chips="activeFilterChips"
            @apply="applyFilters"
            @clear="clearFilters"
            @select-source="selectSource"
            @remove-filter="removeFilter"
          />

          <div
            v-if="loading"
            class="rounded-lais border border-lais-border bg-white px-4 py-12 text-center text-[13px] text-lais-text-muted shadow-lais-card"
          >
            <span
              class="i-lucide-loader-circle mx-auto mb-2 block size-6 animate-spin text-lais-primary"
              aria-hidden="true"
            />
            Carregando imóveis da Área QPreço...
          </div>

          <LaisCard v-else-if="loadError">
            <div class="grid gap-3 py-4 text-[13px] text-lais-text-muted">
              <span
                class="i-lucide-wifi-off size-7 text-rose-600"
                aria-hidden="true"
              />
              <p>Não foi possível carregar os imóveis agora.</p>
              <div>
                <LaisPrimaryButton
                  variant="ghost"
                  icon="i-lucide-refresh-cw"
                  @click="fetchInterests"
                >
                  Recarregar página
                </LaisPrimaryButton>
              </div>
            </div>
          </LaisCard>

          <QprecoListEmptyState
            v-else-if="!interests.length"
            @clear-filters="clearFilters"
            @add-imovel="handleAddImovel"
          />

          <div v-else class="grid gap-4">
            <div
              class="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between"
            >
              <div class="min-w-0">
                <h2 class="text-[16px] font-semibold text-lais-text">
                  Resultados QPreço
                </h2>
                <p class="text-[12px] text-lais-text-muted">
                  Exibindo {{ resultRangeLabel }} de
                  {{ formatNumber(totalResults) }} imóveis em {{ sourceLabel }}.
                </p>
              </div>
              <p
                v-if="externalMeta.total"
                class="text-[12px] font-semibold text-lais-text-muted"
              >
                Base integrada:
                {{ formatNumber(externalMeta.total) }} registros encontrados
              </p>
            </div>

            <div class="grid gap-3">
              <QprecoInterestCard
                v-for="interest in interests"
                :key="interest.id"
                :interest="interest"
                :pricing-report-loading="isPricingReportLoading(interest.id)"
                :pricing-report-unavailable="
                  isPricingReportUnavailable(interest)
                "
                @complement="requestComplement"
                @edit-calculator="goToEditCalculator"
                @pricing-report-click="handlePricingReportLinkClick"
                @access="handleAccess"
                @delete="requestDelete"
              />
            </div>

            <QprecoListPagination
              :query-page="queryPage"
              :total-pages="totalPages"
              :visible-pages="visiblePages"
              @go-to-page="goToPage"
            />
          </div>
        </div>
      </template>
    </main>

    <PricingReportModal
      :show="showPricingReport"
      :title="pricingReportTitle"
      :subtitle="pricingReportSubtitle"
      @close="closePricingReport"
      @cta-clicked="handlePricingReportCtaClicked"
    >
      <PricingReportModule
        v-if="activeReportInterestId"
        :interest-id="activeReportInterestId"
        :initial-section="pricingReportInitialSection"
        @loaded="handlePricingReportLoaded"
        @section-viewed="handlePricingReportSectionViewed"
      />
    </PricingReportModal>

    <LaisConfirmModal
      :show="showDelete"
      title="Quer excluir o imóvel da Área QPreço?"
      description="Você perderá o histórico de estimativas e os comparativos vinculados a este imóvel."
      confirm-text="Excluir"
      :is-loading="isDeleting"
      @close="cancelDelete"
      @confirm="confirmDelete"
    />

    <LaisConfirmModal
      :show="showRevokeShare"
      title="Revogar compartilhamento?"
      description="O link público deixa de abrir o relatório QPreço imediatamente, e a revogação fica registrada na auditoria."
      confirm-text="Revogar"
      :is-loading="revokingShare"
      @close="cancelRevokeShare"
      @confirm="confirmRevokeShare"
    />

    <QprecoComplementModal
      v-model:cep="complementForm.cep"
      v-model:numero="complementForm.numero"
      v-model:complemento="complementForm.complemento"
      :show="showComplement"
      :is-saving="isSavingComplement"
      @close="cancelComplement"
      @submit="submitComplement"
    />
  </div>
</template>
